import gql from 'graphql-tag';
import { useQuery, provideApolloClient } from '@vue/apollo-composable';
import { ref, watch } from 'vue';
import apolloClient from './useApolloClient';

const GET_LEAD_FILES = gql`
  query LeadFilesQuery($leadId: ID!) {
    leadFiles(id: $leadId) {
      date
      name
      type
      interactionid
    }
  }
`;

// Global state management for lead files
const filesCache = ref(new Map());
const loadingStates = ref(new Map());
const errorStates = ref(new Map());

export default function useLeadFilesQuery() {
  provideApolloClient(apolloClient);

  const fetchLeadFiles = (leadId) => {
    if (!leadId) return;

    // Initialize states if not present
    if (!filesCache.value.has(leadId)) {
      filesCache.value.set(leadId, []);
    }
    if (!loadingStates.value.has(leadId)) {
      loadingStates.value.set(leadId, false);
    }
    if (!errorStates.value.has(leadId)) {
      errorStates.value.set(leadId, false);
    }

    // Set loading state
    loadingStates.value.set(leadId, true);
    errorStates.value.set(leadId, false);

    const { result, error, loading, refetch } = useQuery(GET_LEAD_FILES, { leadId }, { fetchPolicy: 'no-cache' });

    // Watch for results
    watch(result, (data) => {
      if (data?.leadFiles) {
        filesCache.value.set(leadId, data.leadFiles);
      }
      loadingStates.value.set(leadId, false);
    });

    // Watch for errors
    watch(error, (err) => {
      if (err) {
        console.error('Error fetching lead files:', err);
        errorStates.value.set(leadId, true);
        loadingStates.value.set(leadId, false);
      }
    });

    return { result, error, loading, refetch };
  };

  const getLeadFiles = (leadId) => {
    return {
      data: filesCache.value.get(leadId) || [],
      loading: loadingStates.value.get(leadId) || false,
      error: errorStates.value.get(leadId) || false,
    };
  };

  const refetchLeadFiles = (leadId) => {
    if (leadId) {
      fetchLeadFiles(leadId);
    }
  };

  return {
    fetchLeadFiles,
    getLeadFiles,
    refetchLeadFiles,
    filesCache,
    loadingStates,
    errorStates,
  };
}
